import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from '../hooks/useAuth';

const TOTAL_INACTIVITY_TIME = 900; // seconds - total time before auto-logout
const WARNING_DURATION = 60; // seconds - how long to show warning dialog

// Derived values (calculated automatically - don't change these)
const INACTIVITY_TIMEOUT = TOTAL_INACTIVITY_TIME * 1000; // ms
const WARNING_TIMEOUT = (TOTAL_INACTIVITY_TIME - WARNING_DURATION) * 1000; // ms

interface AutoLogoutContextType {
    showWarning: boolean;
    timeRemaining: number;
    extendSession: () => void;
}

const AutoLogoutContext = createContext<AutoLogoutContextType | undefined>(undefined);

// Design Notes:

// 1. Keep definitions in the main useEffect as a way to simplify lifecycle management. 
// 2. use refs solve the issue of needing to access definitions from within useEffect
// 3. Also use refs to avoid inconveniences presented by React's lifecycle stuff

export const AutoLogoutProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user, signOut } = useAuth();
    const [showWarning, setShowWarning] = useState(false);
    const [timeRemaining, setTimeRemaining] = useState(WARNING_DURATION);

    const logoutTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const showWarningRef = useRef(false);
    const resetSessionRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        showWarningRef.current = showWarning;
    }, [showWarning]);

    const extendSession = () => {
        if (resetSessionRef.current) {
            resetSessionRef.current();
        }
    };

    const clearAllTimeouts = () => {
        if (logoutTimeoutRef.current) clearTimeout(logoutTimeoutRef.current);
        if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
        if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };

    useEffect(() => {
        if (!user) {
            clearAllTimeouts();
            setShowWarning(false);
            return;
        }

        // Define both functions inside useEffect to avoid dependency issues and stale closures
        const startCountdown = () => {
            setTimeRemaining(WARNING_DURATION);
            countdownIntervalRef.current = setInterval(() => {
                setTimeRemaining(prev => {
                    if (prev <= 1) {
                        signOut();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        };

        const resetSession = () => {
            clearAllTimeouts();
            setShowWarning(false);

            if (!user) return;

            warningTimeoutRef.current = setTimeout(() => {
                setShowWarning(true);
                if (logoutTimeoutRef.current) {
                    clearTimeout(logoutTimeoutRef.current);
                    logoutTimeoutRef.current = null;
                }
                startCountdown();
            }, WARNING_TIMEOUT);

            logoutTimeoutRef.current = setTimeout(() => {
                signOut();
            }, INACTIVITY_TIMEOUT);
        };

        // Store resetSession in ref so extendSession can access it from outside this closure
        resetSessionRef.current = resetSession;

        const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];

        const handleActivity = () => {
            if (!showWarningRef.current) {
                resetSession();
            }
        };

        // Start the initial session timer
        resetSession();

        events.forEach(event => {
            document.addEventListener(event, handleActivity, true);
        });

        return () => {
            clearAllTimeouts();
            events.forEach(event => {
                document.removeEventListener(event, handleActivity, true);
            });
        };
    }, [user, signOut]);

    return (
        <AutoLogoutContext.Provider value={{ showWarning, timeRemaining, extendSession }}>
            {children}
        </AutoLogoutContext.Provider>
    );
};

export const useAutoLogout = () => {
    const context = useContext(AutoLogoutContext);
    if (!context) {
        throw new Error('useAutoLogout must be used within AutoLogoutProvider');
    }
    return context;
}; 